import { useState } from 'react';
import { beginMoveDrag } from '@/shared/lib';
import { Button } from '@/shared/ui';
import type { PortfolioUploadPhoto } from '../model';

interface PhotoItemProps {
  readonly photo: PortfolioUploadPhoto;
  readonly index: number;
  readonly count: number;
  readonly disabled?: boolean;
  readonly onMove: (from: number, to: number) => void;
  readonly onDropPhoto: (source: string, index: number) => void;
  readonly onRepresentative: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onRetry: (id: string) => void;
  readonly onRefresh?: (id: string) => void;
}
export const PortfolioPhotoItem = ({
  photo,
  index,
  count,
  disabled,
  onMove,
  onDropPhoto,
  onRepresentative,
  onRemove,
  onRetry,
  onRefresh,
}: PhotoItemProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const label = `사진 ${index + 1}`;
  return (
    <li
      aria-label={label}
      className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
      draggable={!disabled}
      onDragStart={(event) => {
        if (!disabled) beginMoveDrag(event, photo.localId);
      }}
      onDragOver={(event) => {
        if (!disabled) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (!disabled) onDropPhoto(event.dataTransfer.getData('text/plain'), index);
      }}
    >
      {imageFailed ? (
        <div>
          <p>이미지를 불러오지 못했습니다.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`${label} 이미지 다시 불러오기`}
            disabled={disabled}
            onClick={() => {
              setImageFailed(false);
              onRefresh?.(photo.localId);
            }}
          >
            다시 불러오기
          </Button>
        </div>
      ) : (
        <img
          src={photo.previewUrl}
          alt={`사례 ${label}`}
          className="size-20 rounded object-cover"
          onError={() => setImageFailed(true)}
          draggable={false}
        />
      )}
      <div className="min-w-32 flex-1">
        <p>{index === 0 ? '대표사진' : label}</p>
        <p className="text-sm text-muted-foreground">
          {photo.status === 'PENDING'
            ? '처리 중…'
            : photo.status === 'FAILED'
              ? '처리 실패'
              : '준비 완료'}
        </p>
        {photo.error ? (
          <p role="alert" className="text-sm text-destructive">
            {photo.error}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`${label} 대표 설정`}
          disabled={disabled || index === 0}
          onClick={() => onRepresentative(photo.localId)}
        >
          대표 설정
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`${label} 앞으로 이동`}
          disabled={disabled || index === 0}
          onClick={() => onMove(index, index - 1)}
        >
          앞으로
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`${label} 뒤로 이동`}
          disabled={disabled || index === count - 1}
          onClick={() => onMove(index, index + 1)}
        >
          뒤로
        </Button>
        {photo.status === 'FAILED' ? (
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            aria-label={`${label} 재시도`}
            onClick={() => onRetry(photo.localId)}
          >
            재시도
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          aria-label={`${label} 제거`}
          onClick={() => onRemove(photo.localId)}
        >
          제거
        </Button>
      </div>
    </li>
  );
};
